

import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { db, auth } from "../firebase";
import { collection, onSnapshot, where, query, orderBy } from "firebase/firestore";
import { getAuth, signInWithCustomToken } from "firebase/auth";
import { FiClock, FiMoreVertical } from 'react-icons/fi';



const Content = styled.div`
  padding-top: 100px;
  padding-left: ${({ isSidebarOpen }) => (isSidebarOpen ? '200px' : '0')};
  transition: padding-left 0.3s ease;
  width: 100%;
  background: linear-gradient(to right, #6dd5ed, #2193b0);
  min-height: 100vh;
  font-family: 'Poppins', sans-serif;
  color: black;
`;

const InboxContainer = styled.div`
  width: 90%;
  max-width: 1000px;
  background: white;
  border-radius: 20px;
  padding: 2rem;
  margin: 0 auto;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
`;

const Title = styled.h2`
  text-align: left;
  font-size: 2rem;
  color: #2c3e50;
  margin-bottom: 1.5rem;
`;

const MessageCard = styled.div`
  background: #f9fbff;
  border-left: 6px solid #4a90e2;
  padding: 1rem 1.5rem;
  margin-bottom: 1rem;
  border-radius: 12px;
  transition: all 0.3s ease;
  position: relative;

  &:hover {
    transform: scale(1.01);
    background: #e9f0ff;
  }
`;

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const UserInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const UserName = styled.span`
  font-weight: 600;
  color: #2c3e50;
`;

const UserEmail = styled.span`
  font-size: 0.9rem;
  color: #666;
`;

const MessageText = styled.p`
  font-size: 1rem;
  color: #333;
  margin: 0.5rem 0;
`;

const Timestamp = styled.small`
  color: #666;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const IconWrap = styled.div`
  cursor: pointer;
  position: relative;
`;

const Dropdown = styled.div`
  position: absolute;
  top: 24px;
  right: 0;
  background: #fff;
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 0.5rem 0;
  box-shadow: 0 4px 10px rgba(0,0,0,0.1);
  z-index: 10;
`;

const DropdownItem = styled.div`
  padding: 0.4rem 1rem;
  font-size: 0.9rem;
  color: #333;
  cursor: pointer;
  transition: 0.2s ease;

  &:hover {
    background: #f0f4ff;
  }
`;

const signIntoFirebase = async () => {
  try {
    const res = await fetch('/api/firebase_token', {
      method: "GET",
      credentials: "include",
    });

    const data = await res.json();
    if (!res.ok) throw new Error(data.error || "Failed to get Firebase token");

    const token = data.firebase_token;
    const auth = getAuth();

    const userCredential = await signInWithCustomToken(auth, token);


    const user = userCredential.user;
    await user.getIdToken(true); // `true` forces refresh


    auth.currentUser.getIdTokenResult().then(tokenResult => {
    });

    return user;
  } catch (err) {
    console.error(" Firebase sign-in failed:", err.message);
    return null;
  }
};

const Inbox = ({ isSidebarOpen }) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [mongodbNotifications, setMongodbNotifications] = useState([]);

  // Fetch MongoDB notifications
  const fetchMongodbNotifications = async () => {
    try {
      const response = await fetch('/api/mongodb-notifications', {
        method: 'GET',
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        console.log(response)
        // Format MongoDB notifications to match Firebase notification structure
        const formatted = data.notifications.map(notif => ({
          id: notif.id,
          source: 'mongodb',
          reviewer: notif.reviewer,
          businessName: notif.businessName,
          locationId: notif.locationId,
          message: notif.message,
          isSeen: notif.isSeen,
          timestamp: notif.timestamp ? new Date(notif.timestamp) : new Date(),
          type: 'review_notification'
        }));
        setMongodbNotifications(formatted);
        return formatted;
      } else {
        console.error('Failed to fetch MongoDB notifications');
        return [];
      }
    } catch (err) {
      console.error('Error fetching MongoDB notifications:', err);
      return [];
    }
  };

  useEffect(() => {
    const init = async () => {
      const user = await signIntoFirebase();

      if (!user) {
        setError(" Not signed in to Firebase.");
        setLoading(false);
        return;
      }

      // Fetch MongoDB notifications first
      await fetchMongodbNotifications();

      auth.currentUser.getIdTokenResult().then(tokenResult => {

      }).catch(err => {
        console.error("Failed to get token claims:", err);
      });

      try {
        const user = auth.currentUser;

        if (!user) {
          console.error("User not logged in");
          return;
        }

        // Fetch role from custom claims
        const idTokenResult = await user.getIdTokenResult();
        const role = idTokenResult.claims.role;

        const visibleToValues = [];

        //  Push user's email
        if (user.email) {
          visibleToValues.push(user.email);
        }

        //  If user is admin, allow them to also see admin-level notifications
        if (role === "Admin") {
          visibleToValues.push("Admin");
        }

        const q = query(
          collection(db, "notifications"),
          where("visible_to", "array-contains-any", visibleToValues),
          orderBy("timestamp", "desc")
        );

        const unsubscribe = onSnapshot(
          q,
          async (snapshot) => {
            const firebaseMsgs = snapshot.docs.map((doc) => ({
              id: doc.id,
              source: 'firebase',
              ...doc.data(),
            }));
            
            // Fetch MongoDB notifications again to get latest
            const mongoMsgs = await fetchMongodbNotifications();
            
            // Combine: MongoDB notifications first, then Firebase
            const allMessages = [...mongoMsgs, ...firebaseMsgs];
            
            // Sort by timestamp (newest first) while keeping MongoDB first for same timestamp
            allMessages.sort((a, b) => {
              const timeA = a.timestamp?.toDate ? a.timestamp.toDate() : new Date(a.timestamp);
              const timeB = b.timestamp?.toDate ? b.timestamp.toDate() : new Date(b.timestamp);
              
              // If timestamps are equal, prioritize MongoDB
              if (timeA.getTime() === timeB.getTime()) {
                return a.source === 'mongodb' ? -1 : 1;
              }
              
              return timeB.getTime() - timeA.getTime();
            });
            
            console.log("All notifications fetched:", allMessages);
            setMessages(allMessages);
            setLoading(false);
          },
          (err) => {
            console.error(" Error fetching notifications:", err);
            setError("Failed to load notifications");
            setLoading(false);
          }
        );

        console.log("After onSnapshot setup");

        return unsubscribe;
      } catch (e) {
        console.error(" Snapshot error:", e.message);
        setError("Snapshot error");
        setLoading(false);
      }
    };

    init();
  }, []);

  const handleDelete = (id) => {
    setMessages(messages.filter(msg => msg.id !== id));
    setOpenMenuId(null);
  };

  if (loading) return <Content isSidebarOpen={isSidebarOpen}><div style={{ padding: 40 }}>Loading...</div></Content>;
  if (error) return <Content isSidebarOpen={isSidebarOpen}><div style={{ padding: 40, color: "red" }}>{error}</div></Content>;

  return (
    <Content isSidebarOpen={isSidebarOpen}>
      <InboxContainer>
        {/* <Title>Inbox</Title> */}
        {messages.length === 0 ? (
          <p style={{ textAlign: 'center', color: '#999' }}>No new messages </p>
        ) : (
          messages.map(msg => (
            <MessageCard key={msg.id}>
              <HeaderRow>
                <UserInfo>
                  <UserName>
                    {msg.source === 'mongodb' 
                      ? msg.reviewer || "Unknown Reviewer"
                      : msg.performed_by_name && msg.performed_by
                        ? `${msg.performed_by_name} (${msg.performed_by})`
                        : msg.performed_by || msg.name || msg.email || "Unknown"}
                  </UserName>
                  <UserEmail>
                    {msg.source === 'mongodb' 
                      ? `${msg.businessName || 'Location'}`
                      : msg.email || "No email"}
                  </UserEmail>
                </UserInfo>
              </HeaderRow>
              <MessageText>
                {msg.source === 'mongodb' 
                  ? msg.message || `New review from ${msg.reviewer}`
                  : msg.type === 'branch_request' 
                    ? `${msg.name} '${msg.email}' has requested access to ${msg.branch} branch. Please assign him a role in the user management to allow him in the team.` 
                    : msg.message || "New notification"}
              </MessageText>
              {msg.timestamp && (
                <Timestamp>
                  <FiClock />
                  {(msg.timestamp.toDate ? msg.timestamp.toDate() : new Date(msg.timestamp)).toLocaleString("en-GB", {
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                    hour12: true,
                  }).toUpperCase()}
                </Timestamp>
              )}
            </MessageCard>
          ))
        )}
      </InboxContainer>
    </Content>
  );
};

export default Inbox;